import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Validate Google Analytics ID format to prevent XSS
// Valid formats: G-XXXXXXXX, UA-XXXXXXXX-X, AW-XXXXXXXX
const isValidGaId = (id: string): boolean => {
  const gaIdPattern = /^(G|UA|AW)-[A-Z0-9-]+$/i;
  return gaIdPattern.test(id) && id.length <= 30;
};

export function GoogleAnalytics() {
  const [gaId, setGaId] = useState<string | null>(null);

  useEffect(() => {
    const fetchGaId = async () => {
      try {
        const { data, error } = await supabase
          .from('admin_api_configs')
          .select('config_value')
          .eq('config_key', 'google_analytics_id')
          .maybeSingle();

        if (!error && data?.config_value) {
          // Validate GA ID format before using
          if (isValidGaId(data.config_value)) {
            setGaId(data.config_value);
          } else {
            console.error('Invalid Google Analytics ID format detected. Blocking script injection.');
          }
        }
      } catch (err) {
        console.error('Error fetching Google Analytics ID:', err);
      }
    };

    fetchGaId();
  }, []);

  useEffect(() => {
    if (!gaId) return;

    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${gaId}"]`);
    if (existingScript) return;

    // Add Google Analytics script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag using a safer approach
    const inlineScript = document.createElement('script');
    // Use a self-executing function with the validated gaId
    const safeGaId = gaId.replace(/[^A-Za-z0-9-]/g, '');
    inlineScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${safeGaId}');
    `;
    document.head.appendChild(inlineScript);

    return () => {
      // Cleanup scripts on unmount (optional, usually not needed)
    };
  }, [gaId]);

  return null; // This component doesn't render anything
}
